import JSZip from "jszip";

const MIME: Record<string, string> = {
  htm: "text/html",
  html: "text/html",
  xhtml: "application/xhtml+xml",
  xml: "application/xml",
  ncx: "application/x-dtbncx+xml",
  css: "text/css",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  svg: "image/svg+xml",
  webp: "image/webp",
  woff: "font/woff",
  woff2: "font/woff2",
  ttf: "font/ttf",
  otf: "font/otf",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  smil: "application/smil+xml",
  js: "application/javascript",
  json: "application/json",
  opf: "application/oebps-package+xml",
};

function mimeFromExt(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return MIME[ext] ?? "application/octet-stream";
}

function parseXml(
  text: string,
  mime: DOMParserSupportedType = "text/xml",
): Document {
  return new DOMParser().parseFromString(text, mime);
}

export class Archive {
  private zip!: JSZip;
  private urlCache: Record<string, string> = {};

  async open(
    data: ArrayBuffer | Uint8Array,
    isBase64?: boolean,
  ): Promise<void> {
    this.zip = await new JSZip().loadAsync(data, { base64: !!isBase64 });
  }

  /**
   * Request a file from the archive. Returns parsed content based on type.
   */
  async request(
    url: string,
    type?: "blob" | "json" | "xml" | "xhtml" | "html" | "text",
  ): Promise<Blob | Document | string | unknown> {
    if (type === "blob") return this.getBlob(url);
    const text = await this.getText(url);
    if (!type) {
      const ext = url.split(".").pop()?.toLowerCase() ?? "";
      if (ext === "json") return JSON.parse(text);
      if (["xml", "opf", "ncx"].includes(ext))
        return parseXml(text, "text/xml");
      if (ext === "xhtml") return parseXml(text, "application/xhtml+xml");
      if (["html", "htm"].includes(ext)) return parseXml(text, "text/html");
      return text;
    }
    switch (type) {
      case "json":
        return JSON.parse(text);
      case "xml":
        return parseXml(text, "text/xml");
      case "xhtml":
        return parseXml(text, "application/xhtml+xml");
      case "html":
        return parseXml(text, "text/html");
      default:
        return text;
    }
  }

  async getBlob(url: string, mimeType?: string): Promise<Blob> {
    const decoded = decodeURIComponent(url.replace(/^\//, ""));
    const entry = this.zip.file(decoded);
    if (!entry) throw new Error(`File not found in archive: ${decoded}`);
    const data = await entry.async("uint8array");
    return new Blob([data], { type: mimeType ?? mimeFromExt(decoded) });
  }

  async getText(url: string): Promise<string> {
    const decoded = decodeURIComponent(url.replace(/^\//, ""));
    const entry = this.zip.file(decoded);
    if (!entry) throw new Error(`File not found in archive: ${decoded}`);
    return entry.async("string");
  }

  async getBase64(url: string, mimeType?: string): Promise<string> {
    const decoded = decodeURIComponent(url.replace(/^\//, ""));
    const entry = this.zip.file(decoded);
    if (!entry) throw new Error(`File not found in archive: ${decoded}`);
    const b64 = await entry.async("base64");
    return `data:${mimeType ?? mimeFromExt(decoded)};base64,${b64}`;
  }

  async createUrl(
    url: string,
    options?: { base64?: boolean },
  ): Promise<string> {
    if (this.urlCache[url]) return this.urlCache[url];
    let result: string;
    if (options?.base64) {
      result = await this.getBase64(url);
    } else {
      const blob = await this.getBlob(url);
      result = URL.createObjectURL(blob);
    }
    this.urlCache[url] = result;
    return result;
  }

  revokeUrl(url: string): void {
    const cached = this.urlCache[url];
    if (cached) {
      URL.revokeObjectURL(cached);
      delete this.urlCache[url];
    }
  }

  destroy(): void {
    for (const url of Object.values(this.urlCache)) {
      URL.revokeObjectURL(url);
    }
    this.urlCache = {};
    this.zip = undefined!;
  }
}
